import { getRoles, type GetRolesType } from '@app/queries/roles';
import { salesGroupMapSelector, useFetchConfigDetails, userMapSelector } from '@repo/config-services';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { getUserFullName } from '@repo/utils';
import { Tooltip } from 'antd';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './PermissionAssignedToRenderer.module.scss';

interface AssignedToRendererProps {
  usersUuids: string[];
  salesGroupIds: number[];
  roleIds: number[];
}

function AssignedToRenderer({
  usersUuids = [],
  salesGroupIds = [],
  roleIds = [],
}: AssignedToRendererProps) {
  const [usersTooltipVisible, setUsersTooltipVisible] = useState(false);
  const [groupsTooltipVisible, setGroupsTooltipVisible] = useState(false);
  const [rolesTooltipVisible, setRolesTooltipVisible] = useState(false);
  const userMap = useSelector(userMapSelector);
  const groupMap = useSelector(salesGroupMapSelector);
  const {
    data: rolesData,
  } = useGetGraphqlDataQuery<UseQueryResult<GetRolesType>>({
    document: getRoles,
  }, {
    skip: !rolesTooltipVisible,
  });

  useFetchConfigDetails({
    users: usersTooltipVisible ? usersUuids : [],
  });

  useFetchConfigDetails({
    salesGroups: groupsTooltipVisible ? salesGroupIds : [],
  });

  const hasUsers = usersUuids.length > 0;
  const hasGroups = salesGroupIds.length > 0;
  const hasRoles = roleIds.length > 0;

  const getUserNames = () => {
    if (!usersTooltipVisible) return 'Loading...';
    const names = usersUuids.map(
      (uuid) => getUserFullName(
        { firstName: userMap[uuid]?.firstName, lastName: userMap[uuid]?.lastName },
      ) || '',
    );
    if (names.length > 5) {
      return `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
    }
    return names.join(', ');
  };

  const getGroupNames = () => {
    if (!groupsTooltipVisible) return 'Loading...';
    const names = salesGroupIds.map(
      (id) => groupMap.find((group) => group.id === id)?.name || '',
    );
    if (names.length > 5) {
      return `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
    }
    return names.join(', ');
  };

  const [roleNames, setRoleNames] = useState<string>('Loading...');

  useEffect(() => {
    if (rolesTooltipVisible && rolesData) {
      const names = roleIds.map((id) => rolesData.role.find((role) => Number(role.id) === id)?.name || '');
      setRoleNames(names.length > 5
        ? `${names.slice(0, 5).join(', ')} and ${names.length - 5} more` : names.join(', '));
    }
  }, [rolesTooltipVisible, rolesData, roleIds]);

  const getRoleNames = roleNames;

  return (
    <div className={styles.assignedToContainer}>
      {hasUsers && (
        <Tooltip
          title={getUserNames()}
          trigger="click"
          open={usersTooltipVisible}
          onOpenChange={(visible) => {
            setUsersTooltipVisible(visible);
          }}
        >
          <span className={styles.countItem}>
            {usersUuids.length}
            {' '}
            {pluralize('User', usersUuids.length)}
          </span>
        </Tooltip>
      )}

      {hasGroups && (
        <Tooltip
          title={getGroupNames}
          trigger="click"
          open={groupsTooltipVisible}
          onOpenChange={(visible) => {
            setGroupsTooltipVisible(visible);
          }}
        >
          <span className={styles.countItem}>
            {salesGroupIds.length}
            {' '}
            {pluralize('Group', salesGroupIds.length)}
          </span>
        </Tooltip>
      )}

      {hasRoles && (
        <Tooltip
          title={getRoleNames}
          trigger="click"
          open={rolesTooltipVisible}
          onOpenChange={(visible) => {
            setRolesTooltipVisible(visible);
          }}
        >
          <span className={styles.countItem}>
            {roleIds.length}
            {' '}
            {pluralize('Role', roleIds.length)}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export default AssignedToRenderer;
